import { CloseOutlined } from '@ant-design/icons';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getUsername } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { Divider, Form, Input } from 'antd';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Contact from '../../Common/Contact';
import Loading from '../../Common/Loading';

const ContactSelectionForStaff = ({
  fetchingContacts = false,
  selectionContactList = [],
  onAddStaff = () => {},
  setSelectedStaff,
  selectedStaff,
  hubRecord,
  linkAccountChecked = false,
  formData,
  handleInputChange,
}) => {
  const trans = useSelector(translator);
  // Kept local so keystrokes only re-render the picker, not the host page.
  const [searchNameText, setSearchNameText] = useState('');
  const isSearching = !!searchNameText.trim();

  const filteredContacts = useMemo(() => {
    if (!isSearching) return selectionContactList;
    const query = searchNameText.toLowerCase();
    return selectionContactList.filter((contact) =>
      getUsername(contact).toLowerCase().includes(query)
    );
  }, [isSearching, searchNameText, selectionContactList]);

  // While a query is present the list is shown even with a selection, so a different person can be picked.
  const showContactList = selectedStaff == null || isSearching;

  // Check if the selected staff has a user account
  const staffHasUser = selectedStaff && selectedStaff.useruuid;

  const handleClearSearch = () => {
    setSearchNameText('');
  };

  const handleSelectContact = (contact) => {
    setSelectedStaff(contact);
    onAddStaff(contact);
    handleClearSearch();
  };

  const handleKeyDown = (e, item) => {
    if (e.key === 'Enter') {
      handleSelectContact(item);
    }
  };

  const handleChange = (e) => {
    setSearchNameText(e.target.value);
  };

  return (
    <>
      {linkAccountChecked && (
        <div className="link-social-account">
          {trans('ADDSOCIALCONTACTTO')} {hubRecord?.firstname}
        </div>
      )}
      <div className="new-contact-selection-main-container">
        <div className="new-contact-search-container">
          <label className="select-a-staff-label">
            {linkAccountChecked ? trans('FINDANACCOUNT') : trans('SELECT_A_STAFF_U_MEMEBER')}
          </label>
          <div className="new-contact-search-input">
            <Input
              className="new-search-input"
              value={searchNameText}
              onChange={handleChange}
              placeholder={linkAccountChecked ? trans('ENTER_ACCOUNT_L') : trans('SEARCH_BY_NAME')}
              // Always pass a suffix: toggling it to null makes antd remount the input and drop focus.
              suffix={isSearching ? <CloseOutlined onClick={handleClearSearch} /> : <span />}
            />
          </div>
        </div>
        <div className="new-contact-list-container">
          {fetchingContacts && <Loading />}

          {!fetchingContacts && showContactList && filteredContacts.length === 0 && (
            <div className="no-filtered-contacts-container">
              <span className="no-contact-available">{trans('NOCONTACTSFOUND')}</span>
            </div>
          )}

          {showContactList &&
            filteredContacts.map((item) => {
              const isSelected = selectedStaff?.useruuid === item.useruuid;
              return (
                <div
                  key={item.useruuid}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectContact(item)}
                  onKeyDown={(e) => handleKeyDown(e, item)}
                  onMouseDown={(e) => e.preventDefault()} // Prevents focus loss on click
                  style={{ cursor: 'pointer' }}
                >
                  <Contact
                    contact={item}
                    assignstaff={true}
                    className={isSelected ? 'selected-staff-contact' : 'hover-new-clicable-item'}
                    hubRecord={hubRecord}
                    linkAccountChecked={linkAccountChecked}
                  />
                </div>
              );
            })}

          {!showContactList && selectedStaff.useruuid && (
            <div key={selectedStaff.useruuid}>
              <Contact
                contact={selectedStaff}
                assignstaff={true}
                className="selected-staff-contact"
                hubRecord={hubRecord}
                linkAccountChecked={linkAccountChecked}
              />
            </div>
          )}
        </div>
      </div>
      {/* Only show relationship fields when linkAccountChecked is true AND selectedStaff has a user account */}
      {linkAccountChecked && staffHasUser && (
        <>
          <Divider />
          <div className="relationship-container">
            <div className="label">{trans('RELATIONSHIP')}</div>
            <div className="relationship-content-container">
              <div className="relationship-form-row">
                <span className="relationship-form-row-title generalinfo">{`${hubRecord?.firstname}'${trans('SRELATIONSHIPWITHTH')} ${selectedStaff?.firstname}`}</span>
                <Form.Item name="sourcerelationship" className="relationship-form-row-input-box">
                  <Input
                    placeholder={trans('ENTERTHERELATIONSHIP')}
                    name="sourcerelationship"
                    value={formData?.sourcerelationship || ''}
                    onChange={handleInputChange}
                  />
                </Form.Item>
              </div>
              <div className="relationship-form-row">
                <span className="relationship-form-row-title generalinfo">{`${selectedStaff?.firstname}'${trans('SRELATIONSHIPWITHTHIS')} ${hubRecord?.firstname}`}</span>
                <Form.Item name="targetrelationship" className="relationship-form-row-input-box">
                  <Input
                    placeholder={trans('ENTERTHERELATIONSHIP')}
                    name="targetrelationship"
                    value={formData?.targetrelationship || ''}
                    onChange={handleInputChange}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

ContactSelectionForStaff.propTypes = {
  fetchingContacts: PropTypes.bool,
  selectedStaff: PropTypes.object,
  selectionContactList: PropTypes.array,
  onAddStaff: PropTypes.func.isRequired,
  setSelectedStaff: PropTypes.func.isRequired,
  hubRecord: PropTypes.object,
  linkAccountChecked: PropTypes.bool,
  formData: PropTypes.object,
  handleInputChange: PropTypes.func,
};

export default ContactSelectionForStaff;
